import { apiClient } from "@/lib/api";
import {
  isErrorResponse,
  extractData,
  type ErrorResponse,
  type StandardResponse,
} from "@/types/api-response";
import type {
  ExistingVmCheck,
  ProvisionResourceForUserRequest,
  ProvisionResourceForUserResponse,
} from "@/types/admin-provisioning";

/**
 * Admin provisioning service. Operator creates a VM (Order + Resources + draft Payment)
 * on behalf of a target tenant/user.
 *
 * Note: a dedicated /preview endpoint is deferred (it needs the VM->Disk+Network expansion
 * to be accurate). The authoritative total is returned in the create response.
 */
export const adminProvisioningService = {
  async provisionForUser(
    payload: ProvisionResourceForUserRequest,
  ): Promise<ProvisionResourceForUserResponse> {
    const response = await apiClient.post(
      "/api/v1/admin/resources/provision-for-user",
      payload,
    );
    if (isErrorResponse(response.data)) {
      throw new Error(
        response.data.error?.message ??
          "Failed to provision resource for user.",
      );
    }
    return extractData<ProvisionResourceForUserResponse>(response.data);
  },

  /**
   * Check a machine the infrastructure already runs before binding a VM order to it: whether it
   * exists, whether a resource already records it, and its size for the form to pre-fill.
   */
  async checkExistingVm(vmUuid: string): Promise<ExistingVmCheck> {
    const response = await apiClient.get<
      StandardResponse<ExistingVmCheck> | ErrorResponse
    >(
      `/api/v1/admin/resources/provision-for-user/existing-vm/${encodeURIComponent(vmUuid)}`,
    );
    if (isErrorResponse(response.data)) {
      throw new Error(
        response.data.error?.message ?? "Failed to check the VM.",
      );
    }
    return extractData<ExistingVmCheck>(response.data);
  },

  /**
   * Preview the exact price breakdown (subtotal / cycle discount / VAT / total) for a
   * provision request WITHOUT creating anything. Same pricing path as a real order, so the
   * total matches what the draft payment will be charged.
   */
  async previewProvisionForUser(
    payload: ProvisionResourceForUserRequest,
  ): Promise<ProvisionResourceForUserResponse> {
    const response = await apiClient.post(
      "/api/v1/admin/resources/provision-for-user/preview",
      payload,
    );
    if (isErrorResponse(response.data)) {
      throw new Error(
        response.data.error?.message ?? "Failed to preview pricing.",
      );
    }
    return extractData<ProvisionResourceForUserResponse>(response.data);
  },
};
